/**
 * ExtractionFieldEditor recursively renders a value from the IEP extraction document as the
 * appropriate friendly control:
 *   - booleans  → a yes/no switch
 *   - numbers   → a numeric field
 *   - dates     → a date picker (falls back to text for partial dates like “2025-09”)
 *   - long text → a multi-line box
 *   - short text→ a single-line field
 *   - objects   → a labelled group of nested fields
 *   - arrays    → a list of item cards with Add / Remove
 *
 * Nested objects/arrays and array items render as independently collapsible blocks, colour-tinted
 * by nesting depth so it’s always clear which sub-element you’re inside. Expand/collapse state is
 * owned by the parent (keyed by a stable id) so the quick-links sidebar can reveal a target. Each
 * collapsible block carries that id so links can scroll to it, and focusing any field reports its
 * breadcrumb trail upward.
 *
 * It is presentational: edits flow up via onChange / onAddItem / onRemoveItem, keyed by a
 * lodash-style path so the parent can update one immutable copy of the document.
 */

import clsx from 'clsx'
import format from 'date-fns/format'
import isValid from 'date-fns/isValid'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import makeStyles from '@mui/styles/makeStyles'
import PropTypes from 'prop-types'

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import { parseDateOrNull } from '../../common'
import { addLabel, filterChildKeys, humanizeLabel, idFor, inferKind, isFieldRequired, itemLabel } from './utilities'

import DateField from '../../subcomponents/DateField'

import AddItemButton from './components/AddItemButton'

const isContainer = value => isObject(value)

const useStyles = makeStyles(
  theme => ({
    field: {
      marginTop: theme.spacing(2)
    },

    block: {
      borderWidth: theme.spacing(0.125),
      borderStyle: 'solid',
      borderColor: theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
      marginTop: theme.spacing(1.5),
      overflow: 'hidden',
      scrollMarginTop: theme.spacing(2)
    },

    depth1: {
      backgroundColor: theme.palette.background.paper
    },

    depth2: {
      backgroundColor: theme.palette.neutral.borderLight
    },

    depth3: {
      backgroundColor: theme.palette.neutral.border
    },

    depth4: {
      backgroundColor: theme.palette.neutral.light
    },

    depth5: {
      backgroundColor: theme.palette.neutral.N50
    },

    blockHeader: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1, 1.5),
      userSelect: 'none'
    },

    blockLabel: {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold
    },

    blockBody: {
      padding: theme.spacing(0, 1.5, 1.5)
    },

    chevron: {
      color: theme.palette.text.secondary,
      transition: 'transform 150ms ease'
    },

    chevronOpen: {
      transform: 'rotate(180deg)'
    },

    itemHeaderActions: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between'
    },

    itemHeaderMain: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      flex: 1,
      gap: theme.spacing(0.5),
      minWidth: 0,
      userSelect: 'none'
    },

    itemTitle: {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    scalarRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1)
    },

    scalarRemove: {
      marginTop: theme.spacing(0.5)
    },

    emptyHint: {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
      marginTop: theme.spacing(1)
    },

    addButton: {
      marginTop: theme.spacing(1.5)
    }
  }),
  {
    name: 'ExtractionFieldEditor'
  }
)

const ExtractionFieldEditor = props => {
  const {
    fieldKey,
    value,
    path,
    trail,
    depth,
    inline,
    sectionKey,
    planType,
    disabledKeys,
    excludeKeys,
    onChange,
    onAddItem,
    onRemoveItem,
    onFocusPath,
    isExpanded,
    onToggleExpanded
  } = props

  const isDisabled = disabledKeys?.includes(fieldKey)

  const label = humanizeLabel(fieldKey)
  const kind = inferKind(fieldKey, value, planType)
  const reportFocus = () => onFocusPath?.(trail)

  const handleBooleanChange = event => onChange(path, event.target.checked)
  const handleNumberChange = event => onChange(path, event.target.value === '' ? null : Number(event.target.value))
  const handleTextChange = event => onChange(path, event.target.value)

  const classes = useStyles(props)
  // Cycles through depth1–depth5 classes using modulo arithmetic, so nesting beyond 5 levels
  // repeats the color palette rather than falling off the end of the defined class names.
  const classForDepth = d => classes['depth' + (((d - 1 + 5) % 5) + 1)]

  const renderCollapsibleBlock = children => {
    const id = idFor(path)
    const open = isExpanded(id)
    const handleToggleBlock = () => onToggleExpanded(id)

    return (
      <section id={id} className={clsx(classes.block, classForDepth(depth))}>
        <Box className={classes.blockHeader} onClick={handleToggleBlock}>
          <ExpandMoreIcon className={clsx(classes.chevron, open && classes.chevronOpen)} />
          <Box component="span" className={classes.blockLabel}>
            {label}
          </Box>
        </Box>

        <Collapse in={open} unmountOnExit>
          <Box className={classes.blockBody}>{children}</Box>
        </Collapse>
      </section>
    )
  }

  // A scalar control reported via the same trail its parent handed down.
  switch (kind) {
    case 'boolean':
      return (
        <FormControlLabel
          className={classes.field}
          control={<Switch checked={Boolean(value)} onFocus={reportFocus} onChange={handleBooleanChange} />}
          label={label}
        />
      )

    case 'number':
      return (
        <TextField
          className={classes.field}
          fullWidth
          variant="filled"
          required={isFieldRequired(fieldKey, sectionKey, planType)}
          label={label}
          type="number"
          value={value === null || value === undefined ? '' : value}
          onFocus={reportFocus}
          onChange={handleNumberChange}
        />
      )

    case 'date': {
      const handleDateChange = date => {
        const formattedString = date && isValid(date) ? format(date, 'yyyy-MM-dd') : ''
        onChange(path, formattedString)
      }

      return (
        <Box className={classes.field} onFocus={reportFocus}>
          <DateField
            inputVariant="filled"
            required={isFieldRequired(fieldKey, sectionKey, planType)}
            label={label}
            value={parseDateOrNull(value)}
            onChange={handleDateChange}
          />
        </Box>
      )
    }

    case 'multiline':
      return (
        <TextField
          className={classes.field}
          fullWidth
          multiline
          variant="filled"
          minRows={2}
          required={isFieldRequired(fieldKey, sectionKey, planType)}
          label={label}
          value={value || ''}
          onFocus={reportFocus}
          onChange={handleTextChange}
        />
      )

    case 'object': {
      const childKeys = filterChildKeys(Object.keys(value), { depth, sectionKey, path, excludeKeys, value, planType })

      const childrenUi = childKeys.map(childKey => (
        <ExtractionFieldEditor
          key={childKey}
          fieldKey={childKey}
          value={value[childKey]}
          path={[...path, childKey]}
          trail={[...trail, humanizeLabel(childKey)]}
          depth={depth + 1}
          sectionKey={sectionKey}
          planType={planType}
          disabledKeys={disabledKeys}
          excludeKeys={excludeKeys}
          onChange={onChange}
          onAddItem={onAddItem}
          onRemoveItem={onRemoveItem}
          onFocusPath={onFocusPath}
          isExpanded={isExpanded}
          onToggleExpanded={onToggleExpanded}
        />
      ))

      // The section root (depth 0) and array-item bodies (inline) render their fields directly.
      if (depth === 0 || inline) {
        return <>{childrenUi}</>
      }

      return renderCollapsibleBlock(childrenUi)
    }

    case 'array': {
      const arrayValue = Array.isArray(value) ? value : value ? [value] : []

      // Renders one entry — either a plain string/number row (e.g. behaviours, tags) or a
      // collapsible item card — keyed by its actual index in `arrayValue` so path/onAddItem/
      // onRemoveItem, which are index-based into the underlying array, stay correct even when
      // entries are displayed out of array order (see the accommodation/modification grouping
      // below).
      const renderEntry = (item, index) => {
        if (!isContainer(item)) {
          const isNumberItem = isNumber(item)
          const scalarTrailLabel = itemLabel(fieldKey, item, index)

          const handleScalarFocus = () => onFocusPath?.([...trail, scalarTrailLabel])

          const handleScalarChange = event =>
            onChange(
              [...path, index],
              isNumberItem ? (event.target.value === '' ? null : Number(event.target.value)) : event.target.value
            )

          const handleScalarRemove = () => onRemoveItem(path, index, scalarTrailLabel)

          return (
            <Box key={index} className={classes.scalarRow}>
              <TextField
                fullWidth
                variant="filled"
                type={isNumberItem ? 'number' : 'text'}
                multiline={isString(item) && item.length > 60}
                value={item === null || item === undefined ? '' : item}
                onFocus={handleScalarFocus}
                onChange={handleScalarChange}
              />

              <AddItemButton
                iconOnly
                path={path}
                insertIndex={index}
                label={`Add ${addLabel(fieldKey).toLowerCase()} next to this`}
                onAddItem={onAddItem}
                className={classes.scalarRemove}
              />

              <Tooltip arrow title={`Remove ${addLabel(fieldKey).toLowerCase()}`}>
                <IconButton
                  aria-label="Remove item"
                  size="small"
                  className={classes.scalarRemove}
                  onClick={handleScalarRemove}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )
        }

        const itemId = idFor([...path, index])
        const itemOpen = isExpanded(itemId)

        const handleToggleItem = () => onToggleExpanded(itemId)
        const handleRemoveItem = () => onRemoveItem(path, index, itemLabel(fieldKey, item, index))

        return (
          <section key={index} id={itemId} className={clsx(classes.block, classForDepth(depth + 1))}>
            <Box className={clsx(classes.blockHeader, classes.itemHeaderActions)}>
              <Box className={classes.itemHeaderMain} onClick={handleToggleItem}>
                <ExpandMoreIcon className={clsx(classes.chevron, itemOpen && classes.chevronOpen)} />

                <Box component="span" className={classes.itemTitle}>
                  {itemLabel(fieldKey, item, index)}
                </Box>
              </Box>

              <AddItemButton
                iconOnly
                path={path}
                insertIndex={index}
                label={`Add ${addLabel(fieldKey).toLowerCase()} next to this`}
                onAddItem={onAddItem}
              />

              <Tooltip arrow title={`Remove ${addLabel(fieldKey).toLowerCase()}`}>
                <IconButton aria-label="Remove item" size="small" onClick={handleRemoveItem}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Collapse in={itemOpen} unmountOnExit>
              <Box className={classes.blockBody}>
                <ExtractionFieldEditor
                  fieldKey={fieldKey}
                  value={item}
                  path={[...path, index]}
                  trail={[...trail, itemLabel(fieldKey, item, index)]}
                  depth={depth + 1}
                  inline
                  sectionKey={sectionKey}
                  planType={planType}
                  excludeKeys={excludeKeys}
                  onChange={onChange}
                  onAddItem={onAddItem}
                  onRemoveItem={onRemoveItem}
                  onFocusPath={onFocusPath}
                  isExpanded={isExpanded}
                  onToggleExpanded={onToggleExpanded}
                />
              </Box>
            </Collapse>
          </section>
        )
      }

      // TODO(next milestone): group this section into separate "Accommodations" / "Modifications"
      // subsections (by item.type) instead of one flat list — deferred for now.
      const itemsUi = (
        <>
          {arrayValue.length === 0 && (
            <Box className={classes.emptyHint}>No {addLabel(fieldKey).toLowerCase()} entries yet.</Box>
          )}

          {arrayValue.map((item, index) => renderEntry(item, index))}

          <AddItemButton
            className={classes.addButton}
            path={path}
            label={`Add ${addLabel(fieldKey).toLowerCase()}`}
            onAddItem={onAddItem}
          />
        </>
      )

      if (depth === 0 || inline) {
        return <Box>{itemsUi}</Box>
      }

      return renderCollapsibleBlock(itemsUi)
    }

    case 'text':
    default:
      return (
        <TextField
          className={classes.field}
          fullWidth
          variant="filled"
          disabled={isDisabled}
          required={isFieldRequired(fieldKey, sectionKey, planType)}
          label={label}
          value={value === null || value === undefined ? '' : value}
          onFocus={reportFocus}
          onChange={handleTextChange}
        />
      )
  }
}

ExtractionFieldEditor.propTypes = {
  fieldKey: PropTypes.string.isRequired, // Key name of the field to be rendered.
  value: PropTypes.any, // Current value of the field.
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired, // Array of path segments indexing this field in the main document.
  trail: PropTypes.arrayOf(PropTypes.string).isRequired, // Readable path list representing parent nodes for user-facing headers.
  depth: PropTypes.number.isRequired, // Nesting depth level of the field.
  inline: PropTypes.bool, // If true, the field is inline and shouldn't render a separate layout block wrapper.
  sectionKey: PropTypes.string, // Section key context to toggle special nested layouts (e.g. goals/accommodations).
  planType: PropTypes.string, // Plan type context selecting section vocabularies where they collide (IEP vs. IPP).
  disabledKeys: PropTypes.arrayOf(PropTypes.string), // Array of field keys to render as disabled/read-only.
  excludeKeys: PropTypes.arrayOf(PropTypes.string), // Array of key strings to exclude from form generation.
  onChange: PropTypes.func.isRequired, // Callback triggered when field values change.
  onAddItem: PropTypes.func.isRequired, // Callback triggered to add a new item to list arrays.
  onRemoveItem: PropTypes.func.isRequired, // Callback triggered to delete an item from list arrays.
  onFocusPath: PropTypes.func, // Optional callback triggered when a field receives focus.
  isExpanded: PropTypes.func.isRequired, // Callback to check if a specific collapsible node ID is expanded.
  onToggleExpanded: PropTypes.func.isRequired // Callback to toggle expand/collapse state of a node ID.
}

export default ExtractionFieldEditor
